/**
 * The verbs the roster does not act on itself — a declared-unsupported verb's
 * refusal (D35/D47) and, since D196, an authored verb's ACT.
 *
 * @remarks
 * Split off `narrow-parsed-command.ts` at the file-size cap the way
 * `narrow-intercepts.ts` and `narrow-instrument.ts` were. The two live
 * together because they are the two answers to one question — "the verb is
 * not one of mine; what does the THING say about it?" — one reading
 * `unsupportedVerbNarratives`, the other `verbInteractions`. The loader keeps
 * the two tables disjoint, so a verb reaches exactly one of these. D234
 * adds the one verb that is the engine's on a body and a thing's refusal on
 * a thing (`THING_REFUSED_INTENT_TOKENS`).
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';
import type { ParsedCommand } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandNarrowingOutcome } from './runtime-action.js';

import { requireItem } from '../bundle/bundle-lookups.js';
import { commandInHandOf } from '../conditions/command-in-hand.js';
import { speakSlot } from '../defaults/default-responses.js';
import { itemIdForEntityRef } from '../entities/entity-ref.js';
import { resolveNarrativeLines } from '../narrative/conditional-text.js';
import { authoredActOf } from './authored-act.js';
import { ATTACK_INTENT_TOKEN, GIFT_INTENT_TOKENS } from './narrow-actor-aimed.js';
import { narrowed, rejected } from './narrow-parsed-command.js';

/**
 * D234: the verbs the engine mechanizes on a BODY and refuses OUTRIGHT on a
 * THING — before the roster, in D47's shape. `attack the door` is this: a
 * door is not fought, and the pack's line about kicking it is the better
 * content.
 */
export const THING_REFUSED_OUTRIGHT_INTENT_TOKENS: readonly string[] = [ATTACK_INTENT_TOKEN];

/**
 * Every verb whose refusal on a THING takes D47's shape although the verb is
 * supported — the outright ones above, and (D235) the gift verbs, which the
 * roster answers itself: mechanized when a body receives, and refused in the
 * thing's own words when a thing does, or nobody. The loader's D47 rule
 * reads this list to admit an `unsupportedVerbNarratives` key for any of
 * them: on a thing, the verb reaches exactly the refusal those lines
 * re-voice.
 */
export const THING_REFUSED_INTENT_TOKENS: readonly string[] = [...THING_REFUSED_OUTRIGHT_INTENT_TOKENS, ...GIFT_INTENT_TOKENS];

/**
 * D196: a SUPPORTED verb the roster does not answer is the pack's own word.
 * Aimed at a thing, it is an authored act and the thing's `verbInteractions`
 * decide; aimed at nothing, there is no table to read and the old answer
 * stands — the world knows the word and cannot act on it alone.
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle
 * @param state - The state before the turn, when there is one
 * @param intentText - The canonical verb text
 * @returns The `act` action, or the `unknown_intent` refusal
 */
export const narrowAuthoredVerb = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	state: RuntimeState | undefined,
	intentText: string,
): CommandNarrowingOutcome => {
	if (command.directObjectRef !== undefined) {
		return narrowed(authoredActOf(command, intentText, { itemId: itemIdForEntityRef(command.directObjectRef) }));
	}

	return rejected(bundle, state, { intentToken: intentText, type: 'unknown_intent' }, [
		speakSlot(bundle, state, 'unknownIntent', { intent: intentText.toUpperCase() }),
	]);
};

/**
 * Refuse a recognized-but-unimplemented verb (D35), naming the object when
 * the player named one — "You consider KICK on the security door…" beats a
 * bare refusal, and beats "no directive recognized" by a mile.
 *
 * @param command - The parsed proposal
 * @param bundle - The loaded bundle (response table + item names)
 * @param intentText - The canonical verb text
 * @returns The typed rejection
 */
export const refuseUnsupportedIntent = (
	command: ParsedCommand,
	bundle: EngineGameBundle,
	intentText: string,
	state: RuntimeState | undefined,
): CommandNarrowingOutcome => {
	const objectRef = command.directObjectRef ?? command.indirectObjectRef ?? command.toolRef;
	// D36: quote what the PLAYER typed ("break open"), not the vocabulary's
	// internal name for it — the D26 principle, applied to refusals.
	const intent = command.intentSurface.toUpperCase();
	const verb = command.intentSurface.toLowerCase();

	if (objectRef === undefined) {
		return rejected(bundle, state, { intentToken: intentText, type: 'unsupported_intent' }, [
			speakSlot(bundle, state, 'unsupportedIntent', { intent, verb }),
		]);
	}

	const item = requireItem(bundle, itemIdForEntityRef(objectRef));
	// D47: this THING may have something to say about this verb. Keyed by the
	// canonical token, never the surface, so one `knock` entry answers "rap on
	// the door" too. Absence is silence: no entry, and the slot speaks — and
	// either way the rejection below is identical, because authoring re-voices
	// a refusal and never makes the verb work.
	const authoredLines = item.unsupportedVerbNarratives?.[intentText];
	// D219: this is the one place a thing answers a SENTENCE, so its readings
	// are chosen here, with the command in hand: `give the compass to the
	// bush` and `give the compass to Sylvain` can be told apart. The lines
	// come out as plain strings, which `rejected` passes through unchanged.
	const answeredLines = authoredLines === undefined ? undefined : resolveNarrativeLines(authoredLines, bundle, state, commandInHandOf(command));

	return rejected(bundle, state,
		{ intentToken: intentText, type: 'unsupported_intent' },
		answeredLines ?? [speakSlot(bundle, state, 'unsupportedIntentOnTarget', { intent, itemName: item.name, verb })],
	);
};
